import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useRouter } from 'next/router';
import { Breadcrumb, Container } from 'react-bootstrap';

import { algoliaGetDeals } from '@website/components/LandingPages/algoliaSearch';
import { SalesPopupProvider } from '@website/components/SalesPopupProvider';
import SeoMetaTags from '@website/components/seoMetaTags';
import { TripsSearchResultsContent } from '@website/components/trips/search/TripsSearchResults';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { TripSearchIndex } from '@website/types/TripSearchIndex';

type Props = {
  trips: TripSearchIndex[];
};

export async function getServerSideProps({
  res,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<Props>> {
  const results = await algoliaGetDeals();

  // if there are no results, return a 404
  if (!results.results[0]['hits']) {
    return {
      notFound: true,
    };
  }

  // cache the page for 60 seconds, and revalidate it every 3 minutes
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=180',
  );

  return {
    props: {
      trips: results.results[0]['hits'],
    },
  };
}

export default function DealsPage({ trips }: Props) {
  const router = useRouter();

  // show loading page if the page hasn't been generated, fallback will generate
  // send it to the frontend
  if (router.isFallback) {
    return <LoadingPage />;
  }

  return (
    <>
      <SeoMetaTags
        title={'Latest Deals | Adventure Travel Deals on Skyhook'}
        description={
          "Discover incredible adventure travel deals on hand-picked experiences designed for active people. Don't wait—trips won't stay discounted for long!"
        }
        keywords={'latest deals, adventure travel deals, travel deals'.split(
          ', ',
        )}
        showWebsiteName={false}
        cdn={process.env.NEXT_PUBLIC_CONTENTFUL_IMGIX_URL}
      />

      <style jsx>{`
        .breadcrumb {
          font-size: 16px;
        }
      `}</style>

      <section>
        <Container className="pt-2">
          <Breadcrumb className="py-2">
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active>Latest Deals</Breadcrumb.Item>
          </Breadcrumb>
          <p className="lead">
            Discover incredible deals on hand-picked experiences designed for
            active adventurers. Whether you're dreaming of scaling peaks,
            exploring remote trails, or paddling through stunning landscapes,
            this is where you'll find all the latest adventure deals. Don't
            wait—trips won't stay discounted for long!
          </p>
        </Container>
      </section>

      <section>
        <Container className="py-2">
          <h2>Latest Deals</h2>
          <TripsSearchResultsContent hits={trips} />
        </Container>
      </section>

      <SalesPopupProvider bottomPosition={10} />
    </>
  );
}
